<template>
  <div class="firmware-updates">
    <v-card outlined class="mb-4">
      <v-data-table
          :headers="headers"
          :items="campaigns"
          :loading="loading"
          :items-per-page="10"
          :no-data-text="$t('firmware.updates.none')"
          class="clickable-rows"
          :item-class="item => item.id === campaignId ? 'row--selected' : ''"
          @click:row="open"
      >
        <template #footer.prepend>
          <v-btn v-if="moreAvailable" x-small text :loading="loadingMore" @click="loadMore">{{ $t('firmware.updates.load-more') }}</v-btn>
        </template>
        <template #item.createdAt="{item}">
          {{ formatDate(item.createdAt) }}
          <div class="text-caption text--secondary">{{ item.requestedByName || '-' }}</div>
        </template>
        <template #item.targetVersion="{item}">
          <span class="mono">{{ item.targetVersion }}</span>
          <v-chip v-if="item.forced" x-small color="orange" text-color="white" class="ml-1">{{ $t('firmware.updates.forced') }}</v-chip>
        </template>
        <template #item.chargingPoints="{item}">
          {{ $t('firmware.updates.scope', {count: item.chargingPoints, sites: item.sites}) }}
        </template>
        <template #item.pace="{item}">
          {{ $t('firmware.launch.paces.' + item.pace) }}
          <div class="text-caption text--secondary">{{ item.failureStopsSite ? $t('firmware.updates.failure-stops-site') : $t('firmware.updates.failure-continues') }}</div>
        </template>
        <template #item.when="{item}">
          <template v-if="item.window">{{ item.window.start }} → {{ item.window.end }}</template>
          <template v-else>{{ $t('firmware.updates.anytime') }}</template>
          <div v-if="item.status === 'scheduled'" class="text-caption text--secondary">{{ $t('firmware.updates.from', {date: formatDate(item.startAt)}) }}</div>
        </template>
        <template #item.status="{item}">
          <v-chip x-small :color="statusColorOf(item)" text-color="white">{{ statusLabelOf(item) }}</v-chip>
          <div class="text-caption text--secondary">
            {{ $t('firmware.updates.progress', {done: item.counters.succeeded, total: item.chargingPoints}) }}
            <span v-if="item.counters.failed" class="error--text font-weight-medium"> · {{ $tc('firmware.updates.failed-count', item.counters.failed, {count: item.counters.failed}) }}</span>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Les mises à jour d'une borne seule, lancées depuis sa fiche (hors campagne) -->
    <v-card outlined class="mb-4">
      <v-card-title class="text-subtitle-1">{{ $t('firmware.updates.single-title') }}</v-card-title>
      <v-card-subtitle>{{ $t('firmware.updates.single-hint') }}</v-card-subtitle>
      <v-data-table :headers="singleHeaders" :items="singles" :loading="loadingSingles" :items-per-page="10" :no-data-text="$t('firmware.updates.single-none')" dense>
        <template #footer.prepend>
          <v-btn v-if="moreSingles" x-small text :loading="loadingSingles" @click="loadSingles(singles.length)">{{ $t('firmware.updates.load-more') }}</v-btn>
        </template>
        <template #item.createdAt="{item}">
          <div>{{ formatDate(item.createdAt, 'DD/MM/YY à HH[h]mm') }}</div>
          <div class="text-caption text--secondary">{{ item.requestedByName || '-' }}</div>
        </template>
        <template #item.chargingPointName="{item}">
          <router-link :to="{name: 'charging-points.detail.manage', params: {id: item.chargingPointId}}">{{ item.chargingPointName || item.chargingPointId }}</router-link>
        </template>
        <template #item.targetVersion="{item}">
          <span class="mono">{{ item.fromVersion || '?' }} → {{ item.targetVersion || '?' }}</span>
          <v-chip v-if="item.forced" x-small color="orange" text-color="white" class="ml-1">{{ $t('firmware.engine.forced') }}</v-chip>
        </template>
        <template #item.phase="{item}">
          <v-chip x-small :color="{succeeded: 'success', failed: 'error', cancelled: 'grey'}[item.phase] || 'primary'" text-color="white">{{ $t('firmware.engine.phases.' + item.phase) }}</v-chip>
          <div v-if="item.failureCode" class="text-caption text--secondary">{{ $t('firmware.engine.failures.' + item.failureCode, {detail: item.failureDetail || ''}) }}</div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Le détail d'une mise à jour groupée, dans un panneau à droite : la liste reste visible, le détail défile seul -->
    <!-- Ouvert dès le clic : le détail se charge dedans. Une fenêtre ouverte depuis le panneau le garde ouvert (un clic à
         côté ne ferme qu'elle) ; le panneau fermé, ses fenêtres se ferment avec lui. -->
    <v-navigation-drawer :value="!!campaignId" right fixed temporary :stateless="cancelDialog || releaseDialog" width="760" style="max-width: 100vw" @input="value => !value && closeCampaign()">
    <div v-if="campaignId && !campaignShown" class="d-flex flex-column" style="height: 100%">
      <div class="d-flex justify-end pa-2" style="flex: 0 0 auto">
        <v-btn icon small :aria-label="$t('close')" @click="closeCampaign"><v-icon>mdi-close</v-icon></v-btn>
      </div>
      <div class="d-flex align-center justify-center" style="flex: 1 1 auto">
        <v-progress-circular indeterminate color="primary" size="48" />
      </div>
    </div>
    <v-card v-if="campaignShown" flat>
      <v-card-title class="d-flex flex-wrap" style="gap: 8px">
        <span>{{ $t('firmware.updates.title', {count: campaign.chargingPoints, version: campaign.targetVersion}) }}</span>
        <v-chip small :color="statusColorOf(campaign)" text-color="white">{{ statusLabelOf(campaign) }}</v-chip>
        <v-spacer />
        <v-btn icon small :aria-label="$t('close')" class="order-last" @click="closeCampaign"><v-icon>mdi-close</v-icon></v-btn>
        <template v-if="canDeployFirmware">
          <v-btn v-if="campaign.status === 'running'" small outlined :loading="acting" @click="change('pause')">{{ $t('firmware.updates.pause') }}</v-btn>
          <v-btn v-if="campaign.status === 'paused'" small outlined color="primary" :loading="acting" @click="change('resume')">{{ $t('firmware.updates.resume') }}</v-btn>
          <v-btn v-if="['scheduled', 'running', 'paused'].includes(campaign.status)" small outlined color="error" @click="cancelDialog = true">{{ $t('firmware.updates.cancel') }}</v-btn>
        </template>
      </v-card-title>
      <v-card-subtitle>
        {{ $t('firmware.updates.launched', {date: formatDate(campaign.createdAt), by: campaign.requestedByName || '-'}) }}
        <template v-if="campaign.reason"> · {{ $t('firmware.updates.because', {reason: campaign.reason}) }}</template>
        <template v-if="campaign.pauseAfterFailures"> · {{ $t('firmware.updates.pause-after', {count: campaign.pauseAfterFailures}) }}</template>
      </v-card-subtitle>
      <v-card-text>
        <v-alert v-if="campaign.pauseReason === 'breaker'" type="error" text dense>{{ $t('firmware.updates.breaker', {count: campaign.counters.failed}) }}</v-alert>
        <v-alert v-else-if="campaign.pauseReason === 'user'" type="warning" text dense>{{ $t('firmware.updates.paused-by-user') }}</v-alert>
        <v-alert v-if="campaign.status === 'scheduled'" type="info" text dense>{{ $t('firmware.updates.scheduled-for', {date: formatDate(campaign.startAt)}) }}</v-alert>
        <v-alert v-if="campaign.status === 'cancelled'" type="info" text dense>
          {{ $t('firmware.updates.cancelled', {by: campaign.cancelledBy || '-'}) }}<template v-if="campaign.cancelReason"> · {{ campaign.cancelReason }}</template>
        </v-alert>

        <!-- Un clic sur un état ne montre que ces bornes ; tous les états de la campagne, même rares -->
        <v-row dense class="mb-3">
          <v-col v-for="state in campaignStates" :key="state" cols="6" sm="4" md="2">
            <v-sheet rounded class="pa-3 clickable" :color="memberStateColor(state) + (stateFilter === state ? ' lighten-3' : ' lighten-5')" @click="stateFilter = stateFilter === state ? null : state">
              <div class="text-caption">{{ $t('firmware.updates.states.' + state) }}</div>
              <div class="text-h6">{{ campaign.counters[state] || 0 }}</div>
            </v-sheet>
          </v-col>
        </v-row>
        <div class="d-flex align-center flex-wrap mb-2" style="gap: 8px">
          <span v-if="stateFilter" class="text-caption">{{ $t('firmware.updates.filtered', {state: $t('firmware.updates.states.' + stateFilter).toLowerCase()}) }} <a href="#" @click.prevent="stateFilter = null">{{ $t('firmware.updates.show-all') }}</a></span>
          <v-spacer />
          <v-btn v-if="canDeployFirmware && failedIds.length && !['scheduled', 'running'].includes(campaign.status)" x-small outlined color="primary" @click="retryFailed">
            {{ $tc('firmware.updates.retry-failed', failedIds.length, {count: failedIds.length}) }}
          </v-btn>
        </div>

        <v-sheet v-for="site in shownSites" :key="site.group" outlined rounded class="pa-3 mb-2" :class="{'site--blocked': site.blocked}">
          <div class="d-flex align-center flex-wrap mb-2" style="gap: 8px">
            <b>{{ site.locationName || $t('firmware.updates.no-site') }}</b>
            <span v-if="site.blocked" class="error--text text-caption">{{ $t('firmware.updates.site-blocked') }}</span>
            <v-spacer />
            <v-btn v-if="site.blocked && canDeployFirmware && ['running', 'paused'].includes(campaign.status)" x-small outlined color="warning" @click="askRelease(site)">
              {{ $t('firmware.updates.release') }}
            </v-btn>
          </div>
          <div v-for="chargingPoint in site.shown" :key="chargingPoint.id" class="d-flex align-center flex-wrap py-1" style="gap: 10px">
            <router-link :to="{name: 'charging-points.detail.manage', params: {id: chargingPoint.id}}" style="min-width: 120px">{{ chargingPoint.name }}</router-link>
            <v-chip x-small :color="memberStateColor(chargingPoint.state)" text-color="white">{{ memberLabel(chargingPoint) }}</v-chip>
            <v-chip v-if="chargingPoint.ignored" x-small outlined color="warning">{{ $t('firmware.updates.ignored') }}</v-chip>
            <span class="text-caption text--secondary">{{ memberDetail(chargingPoint) }}</span>
          </div>
        </v-sheet>

        <v-expansion-panels flat class="mt-3">
          <v-expansion-panel>
            <v-expansion-panel-header class="px-0">{{ $t('firmware.updates.log') }}</v-expansion-panel-header>
            <v-expansion-panel-content>
              <div v-for="(entry, index) in campaign.log.slice().reverse()" :key="index" class="text-caption">
                {{ formatDate(entry.at, 'DD/MM/YYYY HH:mm:ss') }} — {{ $t('firmware.updates.log-entries.' + entry.what) }}<template v-if="entry.detail"> · {{ entry.detail }}</template>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card-text>
    </v-card>
    </v-navigation-drawer>

    <v-dialog v-model="releaseDialog" max-width="560">
      <v-card v-if="releasing">
        <v-card-title>{{ $t('firmware.updates.release-title', {site: releasing.locationName || $t('firmware.updates.no-site')}) }}</v-card-title>
        <v-card-text>
          <v-alert type="warning" text dense>{{ $t('firmware.updates.release-warning') }}</v-alert>
          <v-textarea v-model="releaseReason" :label="$t('firmware.updates.release-reason')" rows="2" auto-grow outlined dense />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn small text @click="releaseDialog = false">{{ $t('cancel') }}</v-btn>
          <v-btn small color="warning" :loading="acting" :disabled="!releaseReason.trim()" @click="change('release', releaseReason, releasing.group)">{{ $t('firmware.updates.release') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="cancelDialog" max-width="520">
      <v-card>
        <v-card-title>{{ $t('firmware.updates.cancel-title') }}</v-card-title>
        <v-card-text>
          <p class="text-body-2">{{ $t('firmware.updates.cancel-warning') }}</p>
          <v-textarea v-model="cancelReason" :label="$t('firmware.updates.reason-optional')" rows="2" auto-grow outlined dense />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn small text @click="cancelDialog = false">{{ $t('cancel') }}</v-btn>
          <v-btn small color="error" :loading="acting" @click="change('cancel', cancelReason)">{{ $t('firmware.updates.cancel') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import FirmwareRepository from "@repository/FirmwareRepository";
import FirmwareMixin from "@pages/Firmware/FirmwareMixin";

/** Le moteur repasse chaque minute : la mise à jour ouverte se relit aussi souvent. */
const REFRESH_MS = 30000

/** L'onglet Mises à jour : les lots lancés, et le détail de l'un d'eux site par site. */
export default {
  mixins: [FirmwareMixin],

  data() {
    return {
      campaigns: [],
      loading: false,
      campaign: null,
      acting: false,
      releaseDialog: false,
      releasing: null,
      releaseReason: '',
      cancelDialog: false,
      cancelReason: '',
      timer: null,
      stateFilter: null,
      sitesShown: 20,
      moreAvailable: false,
      loadingMore: false,
      singles: [],
      moreSingles: false,
      loadingSingles: false,
    }
  },

  computed: {
    headers() {
      return [
        {text: this.$t('firmware.updates.columns.launched'), value: 'createdAt'},
        {text: this.$t('firmware.updates.columns.target'), value: 'targetVersion'},
        {text: this.$t('firmware.updates.columns.charging-points'), value: 'chargingPoints'},
        {text: this.$t('firmware.updates.columns.pace'), value: 'pace', sortable: false},
        {text: this.$t('firmware.updates.columns.when'), value: 'when', sortable: false},
        {text: this.$t('firmware.updates.columns.status'), value: 'status'},
      ]
    },
    singleHeaders() {
      return [
        {text: this.$t('firmware.updates.columns.launched'), value: 'createdAt', sortable: false},
        {text: this.$t('firmware.updates.single-charging-point'), value: 'chargingPointName', sortable: false},
        {text: this.$t('firmware.updates.columns.target'), value: 'targetVersion', sortable: false},
        {text: this.$t('firmware.updates.columns.status'), value: 'phase', sortable: false},
      ]
    },
    // Les états présents dans la campagne, dans l'ordre du déroulé (« mises à part », « refusées » compris).
    campaignStates() {
      const order = ['queued', 'running', 'succeeded', 'failed', 'held', 'excluded', 'skipped', 'cancelled']
      return order.filter(state => ['queued', 'running', 'succeeded', 'failed'].includes(state) || this.campaign.counters[state])
    },
    // Les sites, filtrés par état ; une grosse campagne ne montre d'abord que les premiers.
    shownSites() {
      return this.campaign.sites
        .map(site => ({...site, shown: this.stateFilter ? site.chargingPoints.filter(chargingPoint => chargingPoint.state === this.stateFilter) : site.chargingPoints}))
        .filter(site => site.shown.length)
        .slice(0, this.sitesShown)
    },
    failedIds() {
      return this.campaign ? this.campaign.sites.flatMap(site => site.chargingPoints).filter(chargingPoint => chargingPoint.state === 'failed').map(chargingPoint => chargingPoint.id) : []
    },
    // Le lot ouvert, gardé dans l'adresse : le lancement mène droit à lui.
    campaignId() {
      return this.$route.query.campaign || null
    },
    // Le lot affiché est celui de l'adresse : en passant d'un lot à l'autre, l'ancien ne reste pas affiché.
    campaignShown() {
      return !!this.campaign && this.campaign.id === this.campaignId
    },
  },

  watch: {
    campaignId: {
      immediate: true,
      handler() {
        this.closeDialogs()
        this.loadCampaign()
      },
    },
  },

  mounted() {
    this.load()
    this.loadSingles()
    // La liste se relit tant qu'une campagne avance ; la campagne ouverte aussi.
    this.timer = setInterval(() => {
      if (this.campaigns.some(campaign => ['scheduled', 'running', 'paused'].includes(campaign.status))) {
        this.load()
      }
      if (this.singles.some(update => update.open)) {
        this.loadSingles()
      }
      if (this.campaign && (['scheduled', 'running', 'paused'].includes(this.campaign.status) || this.campaign.counters.running)) {
        this.loadCampaign()
      }
    }, REFRESH_MS)
  },

  beforeDestroy() {
    clearInterval(this.timer)
  },

  methods: {
    load() {
      this.loading = true
      FirmwareRepository.campaigns().then(campaigns => {
        // Les pages plus anciennes déjà chargées restent.
        this.campaigns = [...campaigns, ...this.campaigns.slice(campaigns.length).filter(old => !campaigns.some(campaign => campaign.id === old.id))]
        this.moreAvailable = this.moreAvailable || campaigns.length === 50
        this.$emit('loaded', campaigns)
      }).catch(err => {
        this.notifyError(err)
      }).finally(() => {
        this.loading = false
      })
    },

    // Sans décalage : la première page relue (les suivantes déjà chargées restent) ; avec : la page suivante.
    loadSingles(offset = 0) {
      this.loadingSingles = true
      FirmwareRepository.singleUpdates(offset).then(({updates, more}) => {
        this.singles = offset ? [...this.singles, ...updates] : [...updates, ...this.singles.slice(updates.length).filter(old => !updates.some(update => update.id === old.id))]
        this.moreSingles = offset ? more : (this.moreSingles || more)
      }).catch(err => {
        this.notifyError(err)
      }).finally(() => {
        this.loadingSingles = false
      })
    },

    // Finie avec des bornes en échec : ça se voit dans la liste, pas seulement dans le détail.
    finishedWithFailures(campaign) {
      return campaign.status === 'finished' && (campaign.counters.failed || 0) > 0
    },
    statusColorOf(campaign) {
      return this.finishedWithFailures(campaign) ? 'error' : this.campaignStatusColor(campaign.status)
    },
    statusLabelOf(campaign) {
      return this.finishedWithFailures(campaign) ? this.$t('firmware.updates.finished-with-failures') : this.$t('firmware.updates.statuses.' + campaign.status)
    },

    loadMore() {
      this.loadingMore = true
      FirmwareRepository.campaigns(this.campaigns.length).then(campaigns => {
        this.campaigns = [...this.campaigns, ...campaigns]
        this.moreAvailable = campaigns.length === 50
      }).catch(err => {
        this.notifyError(err)
      }).finally(() => {
        this.loadingMore = false
      })
    },

    // Les bornes en échec repartent dans une nouvelle campagne, vers la même version.
    retryFailed() {
      this.$router.push({name: 'firmware.launch', params: {chargingPointIds: this.failedIds, targetVersionId: this.campaign.targetVersionId}})
    },

    loadCampaign() {
      if (!this.campaignId) {
        this.campaign = null
        return
      }
      const id = this.campaignId
      FirmwareRepository.campaign(id).then(campaign => {
        // Une réponse arrivée après qu'on a ouvert un autre lot, ou fermé le panneau, est ignorée.
        if (id === this.campaignId) {
          this.campaign = campaign
        }
      }).catch(err => {
        this.notifyError(err)
        if (id === this.campaignId) {
          this.closeCampaign()
        }
      })
    },

    open(item) {
      this.stateFilter = null
      this.$router.replace({query: {...this.$route.query, campaign: item.id}})
    },

    closeCampaign() {
      if (this.$route.query.campaign) {
        const query = {...this.$route.query}
        delete query.campaign
        this.$router.replace({query})
      }
    },

    // Les fenêtres ouvertes depuis le panneau ne survivent pas à sa fermeture, ni au passage à un autre lot.
    closeDialogs() {
      this.cancelDialog = false
      this.cancelReason = ''
      this.releaseDialog = false
      this.releasing = null
      this.releaseReason = ''
    },

    askRelease(site) {
      this.releasing = site
      this.releaseReason = ''
      this.releaseDialog = true
    },

    change(action, reason = null, location = null) {
      if (!this.campaignShown || this.acting) {
        return
      }
      this.acting = true
      FirmwareRepository.changeCampaignStatus(this.campaign.id, action, reason ? reason.trim() : null, location).then(() => {
        this.notifySuccess('firmware.updates.changed.' + action)
        this.closeDialogs()
        this.loadCampaign()
        this.load()
      }).catch(err => {
        this.notifyError(err)
      }).finally(() => {
        this.acting = false
      })
    },

    // Une borne en cours : l'étape ; les autres : leur état.
    memberLabel(chargingPoint) {
      if (chargingPoint.state === 'running' && chargingPoint.steps.length > 1) {
        return this.$t('firmware.updates.step', {current: chargingPoint.currentStep, total: chargingPoint.steps.length})
      }
      return this.$t('firmware.updates.states.' + chargingPoint.state)
    },

    memberDetail(chargingPoint) {
      if (chargingPoint.failureCode) {
        return this.$t('firmware.engine.failures.' + chargingPoint.failureCode, {detail: chargingPoint.failureDetail || ''})
      }
      if (chargingPoint.reason) {
        return chargingPoint.reason
      }
      if (chargingPoint.waitingReason) {
        return this.$t('firmware.engine.waiting.' + chargingPoint.waitingReason)
      }
      const path = [chargingPoint.fromVersion || '?', ...chargingPoint.steps].join(' → ')
      return chargingPoint.state === 'running' && chargingPoint.phase
        ? `${this.$t('firmware.engine.phases.' + chargingPoint.phase)} · ${path}`
        : path
    },
  },
}
</script>

<style scoped lang="scss">
.firmware-updates {
  .mono {
    font-family: ui-monospace, monospace;
  }
  .site--blocked {
    border-color: var(--v-error-base) !important;
  }
  ::v-deep .row--selected {
    background: #f6f8fa;
  }
  ::v-deep .clickable-rows tbody tr {
    cursor: pointer;
  }
}
</style>
